
import React, { useState } from 'react';
import { VisaRequirementCard } from './VisaRequirementCard';
import { VisaFilter } from './VisaFilter';
import type { VisaRequirement } from './types';
import { STANDARD_REGIONS } from '@/components/search/components/RegionFilter';

interface VisaRequirementsListProps {
  requirements: VisaRequirement[];
}

export const VisaRequirementsList = ({ requirements }: VisaRequirementsListProps) => {
  const [visaTypeFilter, setVisaTypeFilter] = useState<'all' | 'visa_free' | 'visa_on_arrival' | 'e_visa' | 'visa_required'>('all');
  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredRequirements = requirements.filter(req => {
    // Filter by visa type
    const matchesVisaType = visaTypeFilter === 'all' || req.visa_type === visaTypeFilter;
    
    // Filter by region (if selected)
    const matchesRegion = !regionFilter || 
      req.destination_country_details.region === regionFilter;
    
    // Filter by search query
    const matchesSearch = !searchQuery || 
      req.destination_country_details.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesVisaType && matchesRegion && matchesSearch;
  });

  const handleClearRegionFilter = () => {
    setRegionFilter(null);
  };

  const handleClearAllFilters = () => {
    setVisaTypeFilter('all');
    setRegionFilter(null);
    setSearchQuery('');
  };

  return (
    <div>
      <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium mb-3">Filter Requirements</h3>
        <VisaFilter 
          visaTypeFilter={visaTypeFilter}
          regionFilter={regionFilter}
          searchQuery={searchQuery}
          onVisaTypeFilterChange={setVisaTypeFilter}
          onRegionFilterChange={setRegionFilter}
          onSearchChange={setSearchQuery}
        />
      </div>
      
      {/* Show active filters */}
      {(regionFilter || visaTypeFilter !== 'all' || searchQuery) && (
        <div className="flex items-center mt-4 mb-2">
          <span className="text-sm text-gray-600 mr-2">Active filters:</span>
          <div className="flex flex-wrap gap-2">
            {regionFilter && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Region: {regionFilter}
                <button
                  onClick={handleClearRegionFilter}
                  className="ml-1 hover:text-blue-900"
                  aria-label="Remove region filter"
                >
                  ×
                </button>
              </span>
            )}
            {visaTypeFilter !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Type: {visaTypeFilter.replace('_', ' ')}
                <button
                  onClick={() => setVisaTypeFilter('all')}
                  className="ml-1 hover:text-green-900"
                  aria-label="Remove visa type filter"
                >
                  ×
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Search: {searchQuery}
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-1 hover:text-purple-900"
                  aria-label="Remove search filter"
                >
                  ×
                </button>
              </span>
            )}
            <button
              onClick={handleClearAllFilters}
              className="text-xs text-gray-600 hover:text-gray-900 underline ml-2"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4 mt-4">
        {filteredRequirements.length === 0 ? (
          <div className="text-center py-8 border rounded-lg bg-gray-50">
            <p className="text-gray-600">No visa requirements match your filters.</p>
            <button
              onClick={handleClearAllFilters}
              className="mt-2 text-primary-600 hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          filteredRequirements.map((requirement) => (
            <VisaRequirementCard
              key={requirement.id}
              id={requirement.id}
              destinationName={requirement.destination_country_details.name}
              destinationCode={requirement.destination_country_details.code}
              visaType={requirement.visa_type}
              maxStayDays={requirement.max_stay_days}
              notes={requirement.notes}
              region={requirement.destination_country_details.region}
            />
          ))
        )}
      </div>
    </div>
  );
};
